import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, CornerDownLeft, History, Lock, Search, Sparkles } from "lucide-react";
import { floatingSheet, theme } from "../theme";
import { rankEntries } from "./paletteSearch";
import { readRecent, rememberRecent } from "./paletteRecent";
import type { PaletteEntry, PaletteEntryKind } from "./types";

// ── Command palette ───────────────────────────────────────────────────────
// Opened from the search box in the top bar or with Ctrl/Cmd+K, in every shell.
// Sources: every page in the shell's nav model (sidebar, area tabs and More
// tools), the pages above the organisation's tier (they open the TierLockModal),
// things to do (nav/paletteActions.ts), the shell's extras (the consultant's
// clients) and, where the shell offers it, the organisation's own records
// (GET /search). Matching and ranking are nav/paletteSearch.ts. With nothing
// typed it shows what was opened recently and what has something waiting.

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Pages, actions and locked pages. */
  entries: PaletteEntry[];
  /** Loaded once each time the palette opens (e.g. the consultant's client list). */
  extras?: () => Promise<PaletteEntry[]>;
  /** Shown before anything is typed: pages with something waiting. */
  suggested?: PaletteEntry[];
  /** Where recently opened ids are kept (per shell, per browser). */
  recentScope: string;
  /** The organisation's records for a query, paths already resolved. Absent in shells without one organisation. */
  searchRecords?: (query: string, signal: AbortSignal) => Promise<PaletteEntry[]>;
  /** Shortest query that is sent to the record search. */
  recordMinLength?: number;
  placeholder: string;
}

interface Group {
  id: string;
  title: string;
  entries: PaletteEntry[];
  /** A line under the heading instead of rows (record search in flight). */
  note?: string;
}

const LIMITS: Record<PaletteEntryKind, number> = { page: 8, action: 4, client: 6, record: 15, locked: 4 };
const GROUP_TITLES: Record<PaletteEntryKind, string> = {
  page: "Pages", action: "Actions", client: "Clients", record: "Records", locked: "Available on a higher tier",
};
const GROUP_ORDER: PaletteEntryKind[] = ["page", "action", "client", "record", "locked"];
const RECORD_DEBOUNCE_MS = 220;

const kindOf = (e: PaletteEntry): PaletteEntryKind => e.kind ?? "page";

/** The modifier the shortcut uses on this machine: Cmd on a Mac, Ctrl elsewhere. */
export function paletteModifierKey(): string {
  if (typeof navigator === "undefined") return "Ctrl";
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform ?? "";
  return /mac|iphone|ipad/i.test(platform) ? "⌘" : "Ctrl";
}

function KindIcon({ entry }: { entry: PaletteEntry }) {
  const style = { color: theme.color.faint, flexShrink: 0 };
  switch (kindOf(entry)) {
    case "action": return <ArrowRight size={14} style={style} />;
    case "locked": return <Lock size={13} style={style} />;
    case "client": return <Building2 size={14} style={style} />;
    default: return null;
  }
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd style={{ padding: "1px 5px", border: `1px solid ${theme.color.border}`, borderRadius: 3, fontFamily: theme.font }}>{children}</kbd>;
}

export function CommandPalette({
  open, onClose, entries, extras, suggested = [], recentScope, searchRecords, recordMinLength = 2, placeholder,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [extra, setExtra] = useState<PaletteEntry[]>([]);
  const [records, setRecords] = useState<{ query: string; rows: PaletteEntry[] } | null>(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Opening resets the query and focuses the input. The extras load
  // separately, so a client list refreshing while the palette is open (which
  // gives `extras` a new identity) leaves the typed query alone and keeps the
  // current rows until the new list arrives.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setRecords(null);
    setActive(0);
    setRecent(readRecent(recentScope));
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, recentScope]);

  useEffect(() => {
    if (!open || !extras) { setExtra([]); return; }
    let cancelled = false;
    extras().then((list) => { if (!cancelled) setExtra(list); }).catch(() => { if (!cancelled) setExtra([]); });
    return () => { cancelled = true; };
  }, [open, extras]);

  // Records: one request per pause in typing, the previous one abandoned.
  const trimmed = query.trim();
  useEffect(() => {
    if (!open || !searchRecords || trimmed.length < recordMinLength) {
      setRecordsLoading(false);
      return;
    }
    const controller = new AbortController();
    setRecordsLoading(true);
    const t = window.setTimeout(() => {
      searchRecords(trimmed, controller.signal)
        .then((rows) => { if (!controller.signal.aborted) setRecords({ query: trimmed, rows }); })
        .catch(() => { if (!controller.signal.aborted) setRecords({ query: trimmed, rows: [] }); })
        .finally(() => { if (!controller.signal.aborted) setRecordsLoading(false); });
    }, RECORD_DEBOUNCE_MS);
    return () => { window.clearTimeout(t); controller.abort(); };
  }, [open, searchRecords, trimmed, recordMinLength]);

  const groups = useMemo<Group[]>(() => {
    const all = [...entries, ...extra];
    if (!trimmed) {
      const byId = new Map(all.map((e) => [e.id, e]));
      const recentRows = recent.map((id) => byId.get(id)).filter((e): e is PaletteEntry => !!e);
      const recentIds = new Set(recentRows.map((e) => e.id));
      const out: Group[] = [];
      if (recentRows.length) out.push({ id: "recent", title: "Recent", entries: recentRows });
      const waiting = suggested.filter((e) => !recentIds.has(e.id));
      if (waiting.length) out.push({ id: "suggested", title: "Something waiting", entries: waiting.slice(0, 5) });
      if (out.length === 0) out.push({ id: "pages", title: "Pages", entries: all.filter((e) => kindOf(e) === "page").slice(0, 8) });
      return out;
    }

    const ranked = rankEntries(all, trimmed, 60);
    const out: Group[] = [];
    for (const kind of GROUP_ORDER) {
      if (kind === "record") {
        if (!searchRecords || trimmed.length < recordMinLength) continue;
        // Keep the last answer on screen while the next is fetched, so the list does not jump —
        // but only while it is still an answer to roughly the same words.
        const related = !!records && (trimmed.startsWith(records.query) || records.query.startsWith(trimmed));
        const rows = related ? records.rows.slice(0, LIMITS.record) : [];
        if (rows.length) out.push({ id: kind, title: GROUP_TITLES[kind], entries: rows });
        else if (recordsLoading) out.push({ id: kind, title: GROUP_TITLES[kind], entries: [], note: "Searching your records..." });
        continue;
      }
      const rows = ranked.filter((e) => kindOf(e) === kind).slice(0, LIMITS[kind]);
      if (rows.length) out.push({ id: kind, title: GROUP_TITLES[kind], entries: rows });
    }
    return out;
  }, [entries, extra, trimmed, recent, suggested, records, recordsLoading, searchRecords, recordMinLength]);

  const flat = useMemo(() => groups.flatMap((g) => g.entries), [groups]);

  useEffect(() => { setActive(0); }, [trimmed]);
  useEffect(() => { setActive((i) => (i >= flat.length ? 0 : i)); }, [flat.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  if (!open) return null;

  function run(entry: PaletteEntry) {
    const kind = kindOf(entry);
    // Pages and actions only: a record's name is the organisation's data and stays out of the browser's storage.
    if (kind === "page" || kind === "action") rememberRecent(recentScope, entry.id);
    onClose();
    if (entry.onSelect) entry.onSelect();
    else if (entry.to) navigate(entry.to);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      const entry = flat[active];
      if (entry) run(entry);
    }
  }

  const listboxId = "nav-palette-results";
  const optionId = (entry: PaletteEntry) => `nav-palette-${entry.id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
  const nothing = trimmed.length > 0 && flat.length === 0 && !groups.some((g) => g.note);
  let index = -1;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "min(80px, 10vh) 16px 16px" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onKeyDown={onKeyDown}
        style={{
          ...floatingSheet, width: "100%", maxWidth: 580, overflow: "hidden",
          fontFamily: theme.font, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 32px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--c-line)" }}>
          <Search size={15} style={{ color: theme.color.faint, flexShrink: 0 }} />
          <input
            ref={inputRef}
            // Focused as it mounts, so a word typed straight after Ctrl/Cmd+K is not lost.
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-label="Search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={flat[active] ? optionId(flat[active]) : undefined}
            autoComplete="off"
            spellCheck={false}
            style={{ flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", fontSize: 14, color: theme.color.text, fontFamily: theme.font }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, border: `1px solid ${theme.color.border}`, color: theme.color.faint, background: "none", cursor: "pointer", fontFamily: theme.font }}
          >
            esc
          </button>
        </div>

        <div ref={listRef} id={listboxId} role="listbox" aria-label="Results" style={{ overflowY: "auto", flex: 1, minHeight: 0, maxHeight: 440 }}>
          {nothing && (
            <div style={{ padding: 20, fontSize: 13, color: theme.color.faint, textAlign: "center" }}>
              Nothing matches "{trimmed}"
            </div>
          )}
          {groups.map((group) => (
            <div key={group.id} role="group" aria-label={group.title}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px 4px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: theme.color.faint }}>
                {group.id === "recent" && <History size={11} />}
                {group.id === "suggested" && <Sparkles size={11} />}
                {group.title}
              </div>
              {group.note && <div style={{ padding: "4px 16px 10px", fontSize: 12, color: theme.color.faint }}>{group.note}</div>}
              {group.entries.map((entry) => {
                index += 1;
                const i = index;
                const selected = i === active;
                const kind = kindOf(entry);
                const sub = kind === "action" || kind === "locked" ? entry.description ?? entry.hint : entry.hint;
                return (
                  <button
                    key={`${group.id}:${entry.id}`}
                    id={optionId(entry)}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-index={i}
                    onMouseMove={() => { if (!selected) setActive(i); }}
                    onClick={() => run(entry)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      padding: "8px 16px", background: selected ? theme.color.accentBg : "none", border: "none",
                      cursor: "pointer", textAlign: "left", fontFamily: theme.font,
                    }}
                  >
                    <KindIcon entry={entry} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: selected ? theme.color.accentText : kind === "locked" ? theme.color.muted : theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {entry.label}
                      </div>
                      {sub && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
                    </div>
                    {entry.tag && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 999, border: `1px solid ${theme.color.border}`, color: theme.color.muted, flexShrink: 0, whiteSpace: "nowrap" }}>
                        {entry.tag}
                      </span>
                    )}
                    {selected && <CornerDownLeft size={13} style={{ color: theme.color.faint, flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          ))}
          {!trimmed && (
            <div style={{ padding: "10px 16px 12px", fontSize: 11.5, color: theme.color.faint, lineHeight: 1.5 }}>
              {searchRecords
                ? <>Type a page, a task or a record: try <em>risk register</em>, <em>add a supplier</em>, <em>INC-3</em> or <em>A.8.12</em>.</>
                : <>Type the name of a page or a task: try <em>calendar</em> or <em>billing</em>.</>}
            </div>
          )}
        </div>

        <div style={{ padding: "8px 16px", borderTop: `1px solid ${theme.color.border}`, fontSize: 10, color: theme.color.faint, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span><Kbd>Up</Kbd> <Kbd>Down</Kbd> to move</span>
          <span><Kbd>Enter</Kbd> to open</span>
          <span style={{ marginLeft: "auto" }}><Kbd>{paletteModifierKey()}</Kbd> <Kbd>K</Kbd> opens search from anywhere</span>
        </div>
      </div>
    </div>
  );
}
